import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { normaliseDateFormat, type ClinicDateFormat } from '@/components/layout/patientSearch';

interface ClinicBranding {
  name: string;
  logo: string | null;
  primaryColor: string;
  reportFooterText: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  instagram: string;
  openingHours: string;
  qrCodeUrl: string;
  customNextStepsMessage: string;
  nextStepsImageUrl: string;
  aboutTeam: string;
  treatmentApproach: string;
  maintenanceMessage: string;
  // Bundle 2a.2 — marketing surfaces re-added to the SPA patient
  // report so the Puppeteer-rendered PDF carries the same content as
  // the pdfmake legacy. `whatsapp` already lives in clinic settings;
  // `googleReviewUrl` is exposed via the GET /clinics/settings route
  // (backend/src/routes/clinics.ts).
  whatsapp: string;
  googleReviewUrl: string;
  /** Settings → Clinic → Date Format (Incident I-1 Part B: the typeahead card uses it). */
  dateFormat: ClinicDateFormat;
  isLoading: boolean;
}

let cachedBranding: Omit<ClinicBranding, 'isLoading'> | null = null;
let fetchPromise: Promise<void> | null = null;

/**
 * Hook to get clinic branding data (logo, name, colors, contact info).
 * Cached globally so it only fetches once per session.
 */
export function useClinicBranding(): ClinicBranding {
  const [branding, setBranding] = useState<Omit<ClinicBranding, 'isLoading'> | null>(cachedBranding);
  const [isLoading, setIsLoading] = useState(!cachedBranding);

  useEffect(() => {
    if (cachedBranding) {
      setBranding(cachedBranding);
      setIsLoading(false);
      return;
    }

    if (!fetchPromise) {
      fetchPromise = (async () => {
        try {
          const [settingsRes, logoRes] = await Promise.all([
            api.get<{
              name: string;
              address?: string;
              phone?: string;
              email?: string;
              website?: string;
              primaryColor?: string;
              reportFooterText?: string;
              instagram?: string;
              openingHours?: string;
              qrCodeUrl?: string;
              customNextStepsMessage?: string;
              nextStepsImageUrl?: string;
              aboutTeam?: string;
              treatmentApproach?: string;
              maintenanceMessage?: string;
              whatsapp?: string;
              googleReviewUrl?: string;
              dateFormat?: string;
            }>('/clinics/settings'),
            api.get<{ logo: string | null }>('/clinics/logo'),
          ]);

          const d = settingsRes.data;
          cachedBranding = {
            name: d?.name ?? '',
            logo: logoRes.data?.logo ?? null,
            primaryColor: (d?.primaryColor as string) ?? '#2563eb',
            reportFooterText: (d?.reportFooterText as string) ?? '',
            address: (d?.address as string) ?? '',
            phone: (d?.phone as string) ?? '',
            email: (d?.email as string) ?? '',
            website: (d?.website as string) ?? '',
            instagram: (d?.instagram as string) ?? '',
            openingHours: (d?.openingHours as string) ?? '',
            qrCodeUrl: (d?.qrCodeUrl as string) ?? '',
            customNextStepsMessage: (d?.customNextStepsMessage as string) ?? '',
            nextStepsImageUrl: (d?.nextStepsImageUrl as string) ?? '',
            aboutTeam: (d?.aboutTeam as string) ?? '',
            treatmentApproach: (d?.treatmentApproach as string) ?? '',
            maintenanceMessage: (d?.maintenanceMessage as string) ?? '',
            whatsapp: (d?.whatsapp as string) ?? '',
            googleReviewUrl: (d?.googleReviewUrl as string) ?? '',
            dateFormat: normaliseDateFormat(d?.dateFormat),
          };
        } catch {
          cachedBranding = {
            name: '',
            logo: null,
            primaryColor: '#2563eb',
            reportFooterText: '',
            address: '',
            phone: '',
            email: '',
            website: '',
            instagram: '',
            openingHours: '',
            qrCodeUrl: '',
            customNextStepsMessage: '',
            nextStepsImageUrl: '',
            aboutTeam: '',
            treatmentApproach: '',
            maintenanceMessage: '',
            whatsapp: '',
            googleReviewUrl: '',
            dateFormat: 'DD/MM/YYYY',
          };
        }
      })();
    }

    void fetchPromise.then(() => {
      setBranding(cachedBranding);
      setIsLoading(false);
    });
  }, []);

  return {
    name: branding?.name ?? '',
    logo: branding?.logo ?? null,
    primaryColor: branding?.primaryColor ?? '#2563eb',
    reportFooterText: branding?.reportFooterText ?? '',
    address: branding?.address ?? '',
    phone: branding?.phone ?? '',
    email: branding?.email ?? '',
    website: branding?.website ?? '',
    instagram: branding?.instagram ?? '',
    openingHours: branding?.openingHours ?? '',
    qrCodeUrl: branding?.qrCodeUrl ?? '',
    customNextStepsMessage: branding?.customNextStepsMessage ?? '',
    nextStepsImageUrl: branding?.nextStepsImageUrl ?? '',
    aboutTeam: branding?.aboutTeam ?? '',
    treatmentApproach: branding?.treatmentApproach ?? '',
    maintenanceMessage: branding?.maintenanceMessage ?? '',
    whatsapp: branding?.whatsapp ?? '',
    googleReviewUrl: branding?.googleReviewUrl ?? '',
    dateFormat: branding?.dateFormat ?? 'DD/MM/YYYY',
    isLoading,
  };
}

/** Call this to clear the cache (e.g., after updating settings) */
export function clearBrandingCache(): void {
  cachedBranding = null;
  fetchPromise = null;
}
